import {
  Component, Inject, OnDestroy, OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialog, MatDialogRef,
} from '@angular/material/dialog';
import { VerificationComponent } from 'src/app/components/verification/verification.component';
import { CoursesService } from 'src/app/services/courses.service';
import { ToastService } from 'src/app/services/toast.service';

const SCHOOL_COUNSELOR_REQUIREMENT = 'school counselor';
const COUNSELOR_CAREER_GUIDANCE_REQUIREMENT = 'counselor career guidance';
const COUNSELOR_CAREER_GUIDANCE_ERROR_TEXT = 'Counselor - Career Guidance cannot be selected as a course requirement. Select a specific School Counselor child requirement instead.';

const normalizeRequirementName = (value: any): string => (
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

@Component({
  selector: 'app-update-requirement-modal',
  templateUrl: './update-requirement-modal.component.html',
  styleUrls: ['./update-requirement-modal.component.scss'],
})
export class UpdateRequirementModalComponent implements OnInit, OnDestroy {
  requirementOptions: any;
  courseRequirements: any;
  courseDataSubscription: any;
  userCourseRequirementSubscription: any;
  selectedRequirement: any;
  selectedUser: any;
  private blockedRequirementIds = new Set<number>();

  constructor(
    private toastService: ToastService,
    private coursesService: CoursesService,
    public dialogRef: MatDialogRef<UpdateRequirementModalComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit(): void {
    this.selectedUser = this.data.user;
    this.selectedRequirement = this.data.selectedRequirementId;

    // Start with unassigned option as default
    this.requirementOptions = [{ value: null, text: 'Unassigned/Portfolio' }];

    // Get course item requirements to populate the dropdown
    const courseItemId = this.data?.course?.courseItemId;
    if (courseItemId) {
      this.coursesService.getCourseSession(courseItemId, { includedAssociations: 'Requirements' });
      this.courseDataSubscription = this.coursesService.courseSessionData$.subscribe((courseData: any) => {
        if (courseData && courseData.Requirements) {
          this.courseRequirements = courseData.Requirements;

          // Filter course requirements to only include those that exist in user requirements
          const filteredCourseRequirements = this.courseRequirements.filter((courseRequirement: any) => {
            if (this.isBlockedCounselorCareerGuidanceRequirement(courseRequirement)) {
              const blockedId = Number(courseRequirement?.id ?? courseRequirement?.requirementId);
              if (blockedId) {
                this.blockedRequirementIds.add(blockedId);
              }
              return false;
            }

            return this.data?.userRequirements?.findIndex((userRequirement: any) => {
              return courseRequirement?.requirementId.toString() === userRequirement?.requirementId.toString();
            }) !== -1;
          });

          // Map filtered course requirements to dropdown options
          const courseRequirementOptions = filteredCourseRequirements.map((requirement: any) => {
            return {
              value: requirement.id || requirement.requirementId,
              text: requirement.requirement,
            };
          });

          // Combine unassigned option with filtered course requirements
          this.requirementOptions = [
            { value: null, text: 'Unassigned/Portfolio' },
            ...courseRequirementOptions,
          ];

          const selectedOptionExists = this.requirementOptions.some((option: any) => this.isSameRequirementId(option.value, this.selectedRequirement));
          if (!selectedOptionExists || this.isBlockedRequirementId(this.selectedRequirement)) {
            this.selectedRequirement = null;
          }
        }
      });
    }
  }

  setRequirement = (requirement: any) => {
    this.selectedRequirement = requirement;
  }

  updateRequirement = () => {
    if (this.isBlockedRequirementId(this.selectedRequirement)) {
      this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Invalid requirement selection',
          text: COUNSELOR_CAREER_GUIDANCE_ERROR_TEXT,
        },
      });
      return;
    }

    const requirementData = {
      registrationId: this.data.course.id,
      currentStatusKid: this.data.course.statusKid,
      loginName: this.data.selectedUser.loginName,
      requirementId: this.selectedRequirement,
    }

    const userCourseRequirementSubscription = this.coursesService.updateUserCourseRequirement(requirementData).subscribe({
      next: (response: any) => {
        this.toastService.setToast({ text: 'Course Requirement Updated', type: 'success' })
        this.dialog.closeAll();
      },
      error: (error: any) => {
        this.dialog.open(VerificationComponent, {
          data: {
            type: 'alert', title: 'Oops, something went wrong updating the course requirement', text: error.error.message,
          },
        })
      },
    })
  }

  closeDialog = () => {
    this.dialogRef.close();
  }

  private isBlockedRequirementId(requirementId: any): boolean {
    return this.blockedRequirementIds.has(Number(requirementId));
  }

  private isSameRequirementId(firstId: any, secondId: any): boolean {
    if (firstId == null || secondId == null) {
      return firstId == null && secondId == null;
    }
    return Number(firstId) === Number(secondId);
  }

  private isBlockedCounselorCareerGuidanceRequirement(requirement: any): boolean {
    const requirementName = normalizeRequirementName(
      requirement?.requirement ?? requirement?.name ?? requirement?.Requirement?.requirement,
    );
    const topMostParentName = normalizeRequirementName(
      requirement?.topMostParentName ?? requirement?.top_most_parent_name ?? requirement?.Requirement?.topMostParentName,
    );
    const nodeType = requirement?.nodeType ?? requirement?.node_type ?? requirement?.Requirement?.nodeType;

    return (
      requirementName === COUNSELOR_CAREER_GUIDANCE_REQUIREMENT ||
      (nodeType === 'GROUP_NODE' && topMostParentName === SCHOOL_COUNSELOR_REQUIREMENT)
    );
  }

  ngOnDestroy(): void {
    this.courseDataSubscription?.unsubscribe();
    this.userCourseRequirementSubscription?.unsubscribe();
  }

}
